import React, { useState } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  styled,
  Button,
  useMediaQuery,
  useTheme,
  alpha,
  Tooltip,
  Badge,
} from "@mui/material";
import {
  Person as PersonIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Login as LoginIcon,
  AccountCircle as AccountIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import useUser from "../../hooks/api-request/useUser";
import { useTranslation } from "react-i18next";

const StyledMenu = styled(Menu)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 8,
    minWidth: 220,
    boxShadow: theme.shadows[4],
    "& .MuiMenu-list": {
      padding: "8px 0",
    },
    "& .MuiMenuItem-root": {
      padding: theme.spacing(1.5, 2),
      transition: "background-color 0.2s ease",
      "&:hover": {
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
      },
      "& .MuiSvgIcon-root": {
        fontSize: 20,
        marginRight: theme.spacing(1.5),
      },
    },
    
    [theme.breakpoints.down("sm")]: {
      minWidth: 200,
      "& .MuiMenuItem-root": {
        padding: theme.spacing(1.25, 1.5),
      },
    },
  },
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  width: 36,
  height: 36,
  backgroundColor: theme.palette.primary.main,
  cursor: "pointer",
  border: `2px solid transparent`,
  transition: "all 0.2s ease",
  
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
    transform: "scale(1.05)",
    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.4)}`,
  },
  
  [theme.breakpoints.down("sm")]: {
    width: 32,
    height: 32,
  },
  
  [theme.breakpoints.down(400)]: {
    width: 28,
    height: 28,
  },
}));

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#44b700',
    color: '#44b700',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: 'ripple 1.2s infinite ease-in-out',
      border: '1px solid currentColor',
      content: '""',
    },
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(2.4)',
      opacity: 0,
    },
  },
}));

const LoginButton = styled(Button)(({ theme }) => ({
  borderRadius: 20,
  padding: "6px 16px",
  textTransform: "none",
  fontWeight: 500,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  transition: "all 0.2s ease",
  
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
    transform: "translateY(-2px)",
    boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.25)}`,
  },
  
  "&:active": {
    transform: "translateY(0)",
    boxShadow: "none",
  },
  
  [theme.breakpoints.down("sm")]: {
    minWidth: "40px",
    padding: "6px 10px",
  },
  
  [theme.breakpoints.down(400)]: {
    padding: "4px 8px",
    minWidth: "32px",
  },
}));

const LoginIconButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  padding: "8px",
  transition: "all 0.2s ease",
  
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
    transform: "translateY(-2px)",
    boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.25)}`,
  },
  
  "&:active": {
    transform: "translateY(0)",
    boxShadow: "none",
  },
  
  [theme.breakpoints.down(400)]: {
    padding: "6px",
  },
}));

const UserMenu = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { isAuthenticated, logout } = useAuth();
  const { data: user } = useUser();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isExtraSmall = useMediaQuery(theme.breakpoints.down(400));
  const { t } = useTranslation();

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (path) => {
    handleClose();
    navigate(path);
  };

  const handleLogout = async () => {
    handleClose();
    await logout();
    localStorage.removeItem("token");
    navigate("/", { replace: true });
  };

  if (!isAuthenticated) {
    return (
      <Box>
        {isMobile ? (
          <Tooltip title={t('topBar.login')} arrow>
            <LoginIconButton
              onClick={() => navigate("/login")}
              aria-label={t('topBar.login')}
              size={isExtraSmall ? "small" : "medium"}
            >
              <LoginIcon fontSize={isExtraSmall ? "small" : "medium"} />
            </LoginIconButton>
          </Tooltip>
        ) : (
          <LoginButton
            variant="contained"
            startIcon={<LoginIcon />}
            onClick={() => navigate("/login")}
          >
            {t('topBar.login')}
          </LoginButton>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Tooltip title={user?.email || t('topBar.userMenu.account')} arrow>
        <IconButton 
          onClick={handleClick}
          size={isExtraSmall ? "small" : "medium"}
          sx={{ p: isExtraSmall ? 0.5 : 1 }}
        >
          <StyledBadge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            variant="dot"
          >
            <UserAvatar>
              {user?.email ? user.email[0].toUpperCase() : <PersonIcon fontSize={isExtraSmall ? "small" : "medium"} />}
            </UserAvatar>
          </StyledBadge>
        </IconButton>
      </Tooltip>

      <StyledMenu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        elevation={4}
      >
        <MenuItem disabled sx={{ opacity: 0.7 }}>
          <ListItemIcon>
            <AccountIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={user?.email || "User"}
            secondary={user?.first_name || "User"}
            primaryTypographyProps={{ 
              fontWeight: 500, 
              variant: "subtitle1",
              fontSize: isMobile ? "0.875rem" : "1rem",
            }}
            secondaryTypographyProps={{
              fontSize: isMobile ? "0.75rem" : "0.875rem",
            }}
          />
        </MenuItem>

        <Divider />

        <MenuItem onClick={() => handleMenuItemClick("/profile")}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText 
            primary={t('topBar.userMenu.profileSettings')} 
            primaryTypographyProps={{ 
              fontSize: isMobile ? "0.875rem" : "1rem",
            }}
          />
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleLogout} sx={{ color: "error.main" }}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText 
            primary={t('topBar.userMenu.logout')}
            primaryTypographyProps={{ 
              fontSize: isMobile ? "0.875rem" : "1rem",
              fontWeight: 500,
            }}
          />
        </MenuItem>
      </StyledMenu>
    </Box>
  );
};

export default UserMenu;
